import { db, storage, auth } from "./firebase";
import {
    collection,
    addDoc,
    updateDoc,
    deleteDoc,
    doc,
    query,
    orderBy,
    onSnapshot,
    serverTimestamp,
    getDoc,
    getDocs,
    setDoc,
    writeBatch
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { JobApplication, ApplicationStatus, JobDocument, UserProfile, AppNotification } from "@/types";

const COLLECTION = "applications";

export const UserService = {
    getProfile: async (userId: string): Promise<UserProfile | null> => {
        const ref = doc(db, "users", userId);
        const snap = await getDoc(ref);
        if (snap.exists()) {
            return snap.data() as UserProfile;
        }
        return null;
    },

    updateProfile: async (userId: string, data: Partial<UserProfile>) => {
        const ref = doc(db, "users", userId);

        // When initializing or updating, ensure certain fields are set if not present
        const updateData: any = {
            ...data,
            uid: userId,
            updatedAt: serverTimestamp()
        };

        // If this is the superadmin, ensure role is admin
        if (data.email === "jebinjollyabraham@gmail.com") {
            updateData.role = "admin";
        } else if (!data.role) {
            // Default role for others if not already set
            updateData.role = "user";
        }

        // Default plan to free if not set
        if (!data.plan) {
            updateData.plan = "free";
        }

        return setDoc(ref, updateData, { merge: true });
    },


    acceptTerms: async (userId: string, version: string) => {
        const ref = doc(db, "users", userId);
        return setDoc(ref, {
            termsAccepted: true,
            termsAcceptedAt: new Date().toISOString(),
            acceptedTermsVersion: version,
            onboarded: true,
            updatedAt: serverTimestamp()
        }, { merge: true });
    },

    getAllUsers: async (): Promise<UserProfile[]> => {
        const ref = collection(db, "users");
        const snap = await getDocs(ref);
        return snap.docs.map(doc => doc.data() as UserProfile);
    },

    updateUserRoleAndPlan: async (userId: string, role: 'user' | 'admin', plan: 'free' | 'paid') => {
        const ref = doc(db, "users", userId);
        return updateDoc(ref, {
            role,
            plan,
            updatedAt: serverTimestamp()
        });
    },

    deleteProfile: async (userId: string) => {
        // 1. Delete all applications
        const appsRef = collection(db, "users", userId, "applications");
        const snapshot = await getDocs(appsRef);
        const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref));
        await Promise.all(deletePromises);

        // 2. Delete all notifications
        const notifsRef = collection(db, "users", userId, "notifications");
        const notifSnapshot = await getDocs(notifsRef);
        const notifPromises = notifSnapshot.docs.map(doc => deleteDoc(doc.ref));
        await Promise.all(notifPromises);

        // 3. Delete user profile doc
        const ref = doc(db, "users", userId);
        return deleteDoc(ref);
    },

    exportUserData: async () => {
        const user = auth.currentUser;
        if (!user) throw new Error("Not authenticated");
        const idToken = await user.getIdToken();
        const res = await fetch(`/api/users/me/export`, {
            headers: {
                "Authorization": `Bearer ${idToken}`
            }
        });
        if (!res.ok) throw new Error(await res.text());
        return res.blob();
    }
};

export const NotificationService = {
    subscribeToNotifications: (userId: string, callback: (notifications: AppNotification[]) => void) => {
        const q = query(
            collection(db, "users", userId, "notifications"),
            orderBy("createdAt", "desc")
        );

        return onSnapshot(q, (snapshot) => {
            const notifications = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as AppNotification[];
            callback(notifications);
        });
    },

    markAsRead: async (userId: string, notificationId: string) => {
        const ref = doc(db, "users", userId, "notifications", notificationId);
        return updateDoc(ref, { read: true });
    },

    markAsUnread: async (userId: string, notificationId: string) => {
        const ref = doc(db, "users", userId, "notifications", notificationId);
        return updateDoc(ref, { read: false });
    },

    deleteNotification: async (userId: string, notificationId: string) => {
        const ref = doc(db, "users", userId, "notifications", notificationId);
        return deleteDoc(ref);
    },

    clearAllNotifications: async (userId: string) => {
        const q = query(collection(db, "users", userId, "notifications"));
        const snapshot = await getDocs(q);
        const batch = writeBatch(db);
        snapshot.docs.forEach((doc) => {
            batch.delete(doc.ref);
        });
        return batch.commit();
    }
};

export const JobService = {
    // Subscribe to real-time updates
    subscribeToApplications: (userId: string, callback: (apps: JobApplication[]) => void) => {
        const q = query(
            collection(db, "users", userId, COLLECTION),
            orderBy("createdAt", "desc")
        );

        return onSnapshot(q, (snapshot) => {
            const apps = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as JobApplication[];
            callback(apps);
        });
    },

    // Add new application
    addApplication: async (userId: string, data: Omit<JobApplication, "id" | "createdAt" | "updatedAt">) => {
        return addDoc(collection(db, "users", userId, COLLECTION), {
            ...data,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
        });
    },

    // Update status
    updateStatus: async (userId: string, appId: string, status: ApplicationStatus) => {
        const ref = doc(db, "users", userId, COLLECTION, appId);
        return updateDoc(ref, {
            status,
            updatedAt: serverTimestamp()
        });
    },

    // Update Application
    updateApplication: async (userId: string, appId: string, data: Partial<JobApplication>) => {
        const ref = doc(db, "users", userId, COLLECTION, appId);
        return updateDoc(ref, {
            ...data,
            updatedAt: serverTimestamp()
        });
    },

    // Delete application
    deleteApplication: async (userId: string, appId: string) => {
        const ref = doc(db, "users", userId, COLLECTION, appId);
        return deleteDoc(ref);
    },

    // Upload document (max 20 MB)
    uploadDocument: async (userId: string, file: File): Promise<JobDocument> => {
        const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20 MB in bytes

        if (file.size > MAX_FILE_SIZE) {
            throw new Error(`File size exceeds 20 MB limit. Your file is ${(file.size / (1024 * 1024)).toFixed(1)} MB.`);
        }

        const path = `users/${userId}/uploads/${Date.now()}_${file.name}`;
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
        return {
            name: file.name,
            url,
            path,
            uploadedAt: new Date().toISOString()
        };
    },

    // Delete document
    deleteDocument: async (path: string) => {
        const storageRef = ref(storage, path);
        return deleteObject(storageRef);
    },

    // Get document URL (for retrieving items with only storage path)
    getDocumentUrl: async (path: string) => {
        const storageRef = ref(storage, path);
        return getDownloadURL(storageRef);
    }
};

export const WaitlistService = {
    joinWaitlist: async (email: string) => {
        return addDoc(collection(db, "waitlist"), {
            email,
            joinedAt: serverTimestamp()
        });
    }
};

export interface SavedResume {
    id?: string;
    fileName: string;
    storagePath?: string; // Legacy: Some might be stored
    url: string;          // Download URL
    uploadedAt: any;
}

export const ResumeService = {
    // Save resume metadata to library
    saveResume: async (userId: string, resume: SavedResume) => {
        return addDoc(collection(db, "users", userId, "resumes"), {
            ...resume,
            uploadedAt: serverTimestamp()
        });
    },

    // Get all saved resumes
    getResumes: async (userId: string): Promise<SavedResume[]> => {
        const q = query(
            collection(db, "users", userId, "resumes"),
            orderBy("uploadedAt", "desc")
        );
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        })) as SavedResume[];
    },

    // Delete a resume from library
    deleteResume: async (userId: string, resumeId: string) => {
        return deleteDoc(doc(db, "users", userId, "resumes", resumeId));
    }
};

// --- API Gateway Configuration ---
// Using '/api' allows Vite Proxy (dev) or Firebase Hosting (prod) to handle the routing.
const API_BASE = '/api';

async function apiCall(endpoint: string, method: string = 'POST', payload: any = null) {
    const user = auth.currentUser;
    if (!user) throw new Error("Not authenticated");

    const idToken = await user.getIdToken();
    const url = endpoint.startsWith('/') ? `${API_BASE}${endpoint}` : `${API_BASE}/${endpoint}`;

    const options: RequestInit = {
        method,
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${idToken}`
        }
    };

    if (payload && method !== 'GET') {
        options.body = JSON.stringify(payload);
    }

    console.log(`[DEBUG] Fetching: ${method} ${url}`);
    const response = await fetch(url, options);

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || error.error || `Request failed with status ${response.status}`);
    }

    return response.json();
}

export const AdminService = {
    listUsers: () => apiCall('/admin/users', 'GET'),
    addUser: (data: any) => apiCall('/admin/users/add', 'POST', data),
    deleteUser: (uid: string) => apiCall('/admin/users/delete', 'POST', { uid }),
    updateUser: (uid: string, data: any) => apiCall('/admin/users/update', 'POST', { uid, data }),
    sendNotification: (uid: string, title: string, message: string, type: string = 'info') =>
        apiCall('/admin/notifications/send', 'POST', { uid, title, message, type }),
    sendAdvancedNotification: (payload: {
        uid?: string;
        recipientUids?: string[];
        broadcast?: boolean;
        title: string;
        message: string;
        type?: string;
        recipientName?: string;
    }) => apiCall('/admin/notifications/send', 'POST', payload),
    toggleUserStatus: (uid: string, disabled: boolean) =>
        apiCall('/admin/users/toggle-disable', 'POST', { uid, disabled }),

    resetUser: (uid: string) =>
        apiCall('/admin/users/reset', 'POST', { uid }),

    listNotifications: () =>
        apiCall('/admin/notifications/list', 'GET'),

    updateUserFeatures: (uid: string, features: any) =>
        apiCall('/features/update', 'POST', { uid, features }),

    getAnalyticsMetrics: () => apiCall('/admin/analytics/metrics', 'GET'),

    getWaitlist: () => apiCall('/admin/waitlist', 'GET'),
    deleteWaitlistEntry: (id: string) => apiCall('/admin/waitlist/delete', 'POST', { id }),

    // Content Management
    getLandingContent: () => apiCall('/admin/public/content', 'GET'),
    updateLandingContent: (data: any) => apiCall('/admin/content/update', 'POST', data),
    getBlogs: () => apiCall('/admin/public/blogs', 'GET'),
    addBlog: (data: any) => apiCall('/admin/blogs/add', 'POST', data),
    updateBlog: (data: any) => apiCall('/admin/blogs/update', 'POST', data),
    deleteBlog: (id: string) => apiCall('/admin/blogs/delete', 'POST', { id }),
    getBlogComments: (blogId: string) => apiCall(`/admin/blogs/comments?blogId=${blogId}`, 'GET'),
    deleteBlogComment: (blogId: string, commentId: string) => apiCall('/admin/blogs/comments/delete', 'POST', { blogId, commentId }),

    getAnalyticsStats: () => apiCall('/admin/analytics/stats', 'GET'),
    listErrors: (status?: string) => apiCall(`/admin/errors${status ? `?status=${status}` : ''}`, 'GET'),
    updateErrorStatus: (errorId: string, status: string) => apiCall('/admin/errors/update', 'POST', { errorId, status }),

    uploadBlogImage: async (file: File): Promise<string> => {
        const path = `public/blog-images/${Date.now()}_${file.name}`;
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, file);
        return getDownloadURL(storageRef);
    }
};

export const LandingService = {
    getLandingContent: async () => {
        return LandingService.getContent();
    },

    getContent: async () => {
        // Use public fetcher for landing content
        const response = await fetch(`/api/admin/public/content?t=${Date.now()}`);
        if (!response.ok) throw new Error("Failed to fetch landing content");
        return response.json();
    },
    getBlogs: async () => {
        const response = await fetch(`/api/admin/public/blogs?t=${Date.now()}`);
        if (!response.ok) throw new Error("Failed to fetch blogs");
        return response.json();
    },
    addComment: async (blogId: string, authorName: string, content: string) => {
        const response = await fetch('/api/admin/public/blogs/comment', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ blogId, authorName, content })
        });
        if (!response.ok) throw new Error("Failed to add comment");
        return response.json();
    },
    getComments: async (blogId: string) => {
        const response = await fetch(`/api/admin/public/blogs/comments?blogId=${blogId}`);
        if (!response.ok) throw new Error("Failed to fetch comments");
        return response.json();
    }
};

export const FeatureService = {
    checkAccess: (feature: string, uid?: string) =>
        apiCall('/features/check', 'POST', { feature, uid }),

    incrementUsage: (feature: string, uid?: string) =>
        apiCall('/features/increment', 'POST', { feature, uid })
};

export const SupportService = {
    createTicket: async (data: { name: string; email: string; subject: string; message: string }) => {
        // This can be called anonymously or with a token
        const user = auth.currentUser;
        if (user) {
            return apiCall('/support/create', 'POST', data);
        } else {
            const response = await fetch('/api/support/create', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            if (!response.ok) throw new Error("Failed to submit support request");
            return response.json();
        }
    },
    listTickets: () => apiCall('/support/admin/list', 'GET'),
    updateTicketStatus: (ticketId: string, status: string) =>
        apiCall('/support/admin/update', 'POST', { ticketId, status })
};
